import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Sparkles, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1598928506311-c55ded91a20c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

function AuthLayout({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="flex items-center gap-2.5 mb-12">
          <div className="h-9 w-9 rounded-lg bg-teal-600 flex items-center justify-center"><Sparkles className="h-5 w-5 text-white" /></div>
          <span className="font-display text-xl font-semibold">CleanBoss</span>
        </div>
        <div className="max-w-md w-full">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <p className="text-slate-500 mt-2 mb-8">{subtitle}</p>}
          {children}
        </div>
      </div>
      <div className="hidden lg:block relative bg-[#0F172A]">
        <img src={HERO} alt="Freshly cleaned living room" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute bottom-0 inset-x-0 p-12">
          <div className="bg-[#0F172A]/80 backdrop-blur-md rounded-xl p-6 text-white max-w-md border border-white/10">
            <p className="font-display text-xl">Run your whole cleaning business from one place.</p>
            <ul className="mt-4 space-y-2 text-sm text-slate-300">
              {["Schedule recurring cleans without conflicts", "Send estimates clients approve online", "Track invoices, payments and expenses"].map((t) => (
                <li key={t} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-teal-400 mt-0.5" />{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function ErrorBox({ error }) {
  if (!error) return null;
  return <div data-testid="auth-error" role="alert" className="rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2">{error}</div>;
}

function useForm(initial) {
  const [form, setForm] = useState(initial);
  const bind = (k) => ({ value: form[k], onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })) });
  return [form, bind];
}

export function LoginPage() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [form, bind] = useForm({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      setSession({ user: data.user, business: data.business });
      navigate("/");
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to your CleanBoss workspace.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Email" htmlFor="email"><Input id="email" type="email" required autoComplete="email" className="h-11" data-testid="login-email-input" {...bind("email")} /></Field>
        <Field label="Password" htmlFor="password"><Input id="password" type="password" required autoComplete="current-password" className="h-11" data-testid="login-password-input" {...bind("password")} /></Field>
        <ErrorBox error={error} />
        <Button type="submit" disabled={busy} className="w-full h-11" data-testid="login-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Sign in</Button>
        <div className="flex justify-between text-sm">
          <Link to="/forgot-password" className="text-teal-700 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
          <Link to="/register" className="text-slate-700 hover:underline" data-testid="register-link">Create an account</Link>
        </div>
      </form>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [form, bind] = useForm({ name: "", business_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return setError("Password must be at least 8 characters.");
    setBusy(true); setError("");
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { data } = await api.post("/auth/register", { ...form, timezone });
      setSession({ user: data.user, business: data.business });
      navigate("/");
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <AuthLayout title="Start your free trial" subtitle="14 days free. No card required.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field label="Your name" htmlFor="name"><Input id="name" required className="h-11" data-testid="register-name-input" {...bind("name")} /></Field>
        <Field label="Business name" htmlFor="bname"><Input id="bname" required className="h-11" data-testid="register-business-input" {...bind("business_name")} /></Field>
        <Field label="Email" htmlFor="remail"><Input id="remail" type="email" required className="h-11" data-testid="register-email-input" {...bind("email")} /></Field>
        <Field label="Password" htmlFor="rpass" hint="At least 8 characters."><Input id="rpass" type="password" required className="h-11" data-testid="register-password-input" {...bind("password")} /></Field>
        <ErrorBox error={error} />
        <Button type="submit" disabled={busy} className="w-full h-11" data-testid="register-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create account</Button>
        <p className="text-sm text-slate-600">Already have an account? <Link to="/login" className="text-teal-700 hover:underline" data-testid="login-link">Sign in</Link></p>
      </form>
    </AuthLayout>
  );
}

export function ForgotPage() {
  const [form, bind] = useForm({ email: "" });
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { const { data } = await api.post("/auth/forgot-password", form); setMsg(data.message); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <AuthLayout title="Reset your password" subtitle="We'll email you a secure link that expires in 1 hour.">
      <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
        <Field label="Email" htmlFor="femail"><Input id="femail" type="email" required className="h-11" data-testid="forgot-email-input" {...bind("email")} /></Field>
        <ErrorBox error={error} />
        {msg && <div data-testid="forgot-success" className="rounded-lg border border-teal-200 bg-teal-50 text-teal-900 text-sm px-3 py-2">{msg}</div>}
        <Button type="submit" disabled={busy} className="w-full h-11" data-testid="forgot-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Send reset link</Button>
        <Link to="/login" className="text-sm text-teal-700 hover:underline" data-testid="back-to-login-link">Back to sign in</Link>
      </form>
    </AuthLayout>
  );
}

export function ResetPage() {
  const [params] = useSearchParams();
  const [form, bind] = useForm({ password: "", confirm: "" });
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return setError("Passwords do not match.");
    setBusy(true); setError("");
    try { const { data } = await api.post("/auth/reset-password", { token: params.get("token") || "", password: form.password }); setMsg(data.message); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <AuthLayout title="Choose a new password">
      <form onSubmit={submit} className="space-y-4 mt-6" data-testid="reset-form">
        <Field label="New password" htmlFor="np" hint="At least 8 characters."><Input id="np" type="password" required minLength={8} className="h-11" data-testid="reset-password-input" {...bind("password")} /></Field>
        <Field label="Confirm password" htmlFor="cp"><Input id="cp" type="password" required className="h-11" data-testid="reset-confirm-input" {...bind("confirm")} /></Field>
        <ErrorBox error={error} />
        {msg && <div data-testid="reset-success" className="rounded-lg border border-teal-200 bg-teal-50 text-teal-900 text-sm px-3 py-2">{msg} <Link to="/login" className="underline">Sign in</Link></div>}
        <Button type="submit" disabled={busy || !!msg} className="w-full h-11" data-testid="reset-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Update password</Button>
      </form>
    </AuthLayout>
  );
}
